import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Camera, Phone } from "lucide-react";
import { BRAND } from "../lib/api";

/**
 * Floating "Text us a photo" button.
 * Visible on all devices; on mobile it opens the SMS app (MMS attach) with a
 * pre-filled message. On desktop it shows a tooltip prompting the user to
 * text from their phone (or call).
 */
export default function TextUsButton() {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // reveal after hero
    const onScroll = () => setVisible(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll);
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    setIsMobile(/Android|iPhone|iPad|iPod|Mobile/i.test(ua));
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const smsBody = encodeURIComponent(
    "Hi RRR Paint — I'd like a free quote. [Attach a photo of the area]"
  );
  // iOS uses &body=, Android accepts ?body=
  const smsHref = `sms:+13342019444${isMobile && /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "&" : "?"}body=${smsBody}`;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.9 }}
          transition={{ duration: 0.5, ease: [0.6, 0.05, 0.15, 1] }}
          className="fixed z-40 bottom-5 right-5 md:bottom-8 md:right-8"
          data-testid="text-us-widget"
        >
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="absolute bottom-full right-0 mb-3 w-[300px] border border-brand-border bg-brand-surface/95 backdrop-blur-xl p-5"
                data-testid="text-us-card"
              >
                <button
                  onClick={() => setExpanded(false)}
                  className="absolute top-3 right-3 text-brand-muted hover:text-white"
                  aria-label="close"
                  data-testid="text-us-close"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-brand-blue mb-3">
                  <Camera className="h-3.5 w-3.5" />
                  Faster than a form
                </div>
                <h4 className="font-display uppercase text-2xl leading-none mb-2">
                  Text us a <span className="text-brand-blue">photo.</span>
                </h4>
                <p className="text-brand-muted text-sm mb-4">
                  Snap the wall, room, or exterior. We&apos;ll reply with a
                  ballpark quote — no form required.
                </p>
                <a
                  href={smsHref}
                  data-testid="text-us-sms-link"
                  className="flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 text-sm transition-colors"
                >
                  <MessageSquare className="h-4 w-4" />
                  Text 334-201-9444
                </a>
                <a
                  href={BRAND.phoneHref}
                  className="mt-2 flex items-center justify-center gap-2 border border-brand-border hover:border-brand-blue text-white/90 font-display uppercase tracking-widest py-3 text-sm transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Or call now
                </a>
                {!isMobile && (
                  <p className="mt-3 text-[10px] tracking-widest uppercase text-brand-muted text-center">
                    Tap on your phone to open messages
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            onClick={() => setExpanded((v) => !v)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="text-us-btn"
            className="group flex items-center gap-3 bg-brand-blue hover:bg-brand-blueHover text-white pl-4 pr-5 py-4 shadow-[0_10px_40px_rgba(30,77,219,0.35)] font-display uppercase tracking-widest text-sm"
            aria-label="Text us a photo"
          >
            <span className="relative">
              <MessageSquare className="h-5 w-5" strokeWidth={2.2} />
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-white animate-pulse" />
            </span>
            <span className="hidden sm:inline">Text a photo</span>
            <span className="sm:hidden">Text us</span>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
